import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


import { useCases } from "@/hooks/useCases";
import { reviewerApi, type ApiError, type CaseSummary } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import {
  ArrowRight,
  ArrowUpRight,
  Archive,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileText,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AddApplicantDialog } from "@/components/AddApplicantDialog";
import { MorningSnapshot, type SnapshotFilter } from "@/components/MorningSnapshot";
import { VerificationReasons } from "@/components/VerificationReasons";
import { isNeedsAttention } from "@/lib/morningSnapshot";
import { toTitleCase } from "@/lib/utils";
import { resolveFollowUpReason } from "@/lib/followUpReasons";
import { bucketReason } from "@/lib/verificationReasons";
import { initClarity } from "@/lib/clarity";
import { parseDateQuery } from "@/lib/searchDate";
import {
  describeApplicantIdvState,
  isQueueWorthyIdvState,
} from "@/lib/applicantIdvState";
import { isUnderRequiredIdvReview, REQUIRED_IDV_REVIEW_LABEL } from "@/lib/requiredIdv";
import { ApplicantIDVStateBadge } from "@/components/ApplicantIDVStateBadge";

import {
  buildWhatsHappening,
  formatAbsoluteDate,
  formatAbsoluteDateTime,
  formatRelativeTime,
  getCaseStatus,
  isClosedCase,
  STATUS_FILTER_OPTIONS,
  SUMMARY_ORDER,
  FILTER_TOOLTIP,
  type StatusKind,
} from "@/lib/caseStatus";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// Entrance fades were causing a perceived "double load" — skeleton/loading
// state dismissing then content re-fading in from below. Render loaded
// content in place instead. Motion wrappers kept to minimize churn.
const fadeUp = (_delay: number) => ({
  initial: false as const,
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0 },
});

const PAGE_SIZE = 25;

const JOURNEY_STEPS = [
  {
    title: "Application",
    body: "Applicants complete a guided, calmer intake that feels more like progress than paperwork.",
    icon: FileText,
    tone: "bg-white text-slate-900 border-slate-200",
  },
  {
    title: "Trust checkpoint",
    body: "Identity, account history, and vouch signals are collected into one clean signal layer.",
    icon: ShieldCheck,
    tone: "bg-gradient-to-br from-violet-600 to-indigo-600 text-white border-violet-500 shadow-violet-500/20",
  },
  {
    title: "Recruiter review",
    body: "Recruiters see a clearer first read with less chasing and more confident follow-up.",
    icon: Users,
    tone: "bg-white text-slate-900 border-slate-200",
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => { initClarity(); }, []);
  const [addOpen, setAddOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<CaseSummary | null>(null);
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusKind | "all">("all");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [waitingOnVouchOnly, setWaitingOnVouchOnly] = useState(false);
  const [reasonFilter, setReasonFilter] = useState<{ key: string; label: string } | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Always fetch closed cases so the Closed pill count is accurate
  // regardless of the active filter. Closed rows are hidden from the table
  // unless the user explicitly picks the Closed filter.
  const { cases, loading, refetch } = useCases({ includeClosed: true });
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setLastUpdated(Date.now());
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const updatedAgo = formatRelativeTime(new Date(lastUpdated).toISOString());

  // Counts power the status summary strip (priority order).
  const counts = useMemo(() => {
    const map: Record<StatusKind, number> = {
      action_required: 0,
      submitted: 0,
      awaiting_vouches: 0,
      in_progress: 0,
      pending: 0,
      verified: 0,
      not_verified: 0,
      closed: 0,
    };
    for (const c of cases) {
      const kind = getCaseStatus(c).kind;
      // The table hides closed rows unless the Closed filter is active, so
      // counts for non-closed kinds must also exclude closed cases —
      // otherwise the badge says "3" but the filtered view shows fewer.
      if (kind !== "closed" && isClosedCase(c)) continue;
      map[kind] += 1;
    }
    return map;
  }, [cases]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const filtered = cases
      .filter((c) => (statusFilter === "closed" ? isClosedCase(c) : !isClosedCase(c)))

      .filter((c) => {
        if (statusFilter === "all") return true;
        return getCaseStatus(c).kind === statusFilter;
      })
      .filter((c) => (attentionOnly ? isNeedsAttention(c, now) : true))
      .filter((c) =>
        waitingOnVouchOnly
          ? getCaseStatus(c).kind === "action_required" && (c.current_step ?? 0) >= 5
          : true
      )
      .filter((c) => (reasonFilter ? bucketReason(c.follow_up_reason) === reasonFilter.key : true))
      .filter((c) => {
        if (!q) return true;
        const dateQuery = parseDateQuery(search);
        if (dateQuery) {
          return dateQuery.match(c.created_at ?? c.updated_at);
        }
        const name = (c.applicant_name ?? "").toLowerCase();
        const property = (c.property ?? "").toLowerCase();
        const reasonObj = resolveFollowUpReason(c.follow_up_reason);
        const reasonValue = (c.follow_up_reason ?? "").toLowerCase();
        const reasonLabel = (reasonObj?.label ?? "").toLowerCase();
        return (
          name.includes(q) ||
          property.includes(q) ||
          reasonValue.includes(q) ||
          reasonLabel.includes(q)
        );
      });

    return [...filtered].sort((a, b) => {
      const sa = getCaseStatus(a);
      const sb = getCaseStatus(b);
      if (sa.priority !== sb.priority) return sa.priority - sb.priority;
      const ta = new Date(a.updated_at ?? a.created_at ?? 0).getTime();
      const tb = new Date(b.updated_at ?? b.created_at ?? 0).getTime();
      return tb - ta;
    });
  }, [cases, statusFilter, attentionOnly, waitingOnVouchOnly, reasonFilter, search]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, sorted.length);
  const pageCases = sorted.slice(startIdx, endIdx);

  // Warm the react-query cache for an applicant's review + inquiry result so
  // navigating to /applicant/:id renders without waiting on the ~3s
  // /cases/{id}/review call. Safe to call repeatedly — prefetchQuery is a
  // no-op when the data is already fresh (staleTime 30s).
  const prefetchApplicant = (c: CaseSummary) => {
    const id = c.inquiry_id || c.session_id;
    if (!id) return;
    queryClient.prefetchQuery({
      queryKey: ["reviewer", "case-review", id],
      queryFn: () => reviewerApi.getCaseReview(id),
      staleTime: 30_000,
    });
    queryClient.prefetchQuery({
      queryKey: ["reviewer", "inquiry-result", id],
      queryFn: () => reviewerApi.getInquiryResult(id),
      staleTime: 30_000,
    });
  };

  const openCase = (c: CaseSummary) => {
    const id = c.inquiry_id || c.session_id;
    if (id) navigate(`/applicant/${id}`);
  };


  const confirmClose = async () => {
    if (!closeTarget) return;
    setClosing(true);
    const trimmedReason = closeReason.trim();
    try {
      await reviewerApi.closeCase(
        closeTarget.inquiry_id,
        trimmedReason.length > 0 ? trimmedReason : undefined,
      );
      await queryClient.invalidateQueries({ queryKey: ["reviewer", "sessions"] });
      toast({
        title: "Case closed",
        description: `${toTitleCase(closeTarget.applicant_name)} was archived from the queue.`,
      });
      setCloseTarget(null);
      setCloseReason("");
      refetch();
    } catch (e) {
      const err = e as ApiError;
      toast({
        title: "Could not close case",
        description: err.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setClosing(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setAttentionOnly(false);
    setWaitingOnVouchOnly(false);
    setReasonFilter(null);
    setPage(1);
  };

  const hasActiveFilters =
    search.trim().length > 0 || statusFilter !== "all" || attentionOnly || waitingOnVouchOnly || reasonFilter !== null;

  const openCases = cases.filter((c) => !isClosedCase(c)).length;
  const verifiedCount = counts.verified;
  const reviewCount = counts.submitted;
  const vouchCount = counts.awaiting_vouches;

  const handleSnapshotFilter = (kind: SnapshotFilter) => {
    setPage(1);
    if (kind === "needs_attention") {
      setStatusFilter("all");
      setAttentionOnly(true);
      setWaitingOnVouchOnly(false);
    } else if (kind === "waiting_on_vouch") {
      setStatusFilter("action_required");
      setAttentionOnly(false);
      setWaitingOnVouchOnly(true);
    } else {
      setAttentionOnly(false);
      setWaitingOnVouchOnly(false);
      setStatusFilter(kind);
    }
    // Scroll the queue into view so the change is obvious.
    if (typeof document !== "undefined") {
      document.getElementById("applicant-list")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="space-y-6">
      <motion.section {...fadeUp(0)} className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white/90 p-6 sm:p-8 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-16 -top-20 h-56 w-56 rounded-full bg-violet-500/10 blur-3xl" />
            <div className="absolute right-0 top-6 h-44 w-44 rounded-full bg-sky-400/10 blur-3xl" />
          </div>

          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <Badge
              variant="outline"
              className="rounded-full border-violet-200 bg-violet-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-violet-700"
            >
              Trust checkpoint by DigaVouch
            </Badge>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
              <ArrowUpRight className="h-3.5 w-3.5 text-violet-600" />
              Updated {updatedAgo}
            </span>
          </div>

          <div className="relative mt-6 max-w-3xl">
            {/* This page has a NAME, and the name is load-bearing.
                `queueBackLabel` in @/lib/verticalCopy forks on exactly that:
                the leasing queue's back-link says "Back to Verification
                Center" because this <h1> is here, while the recruiting
                dashboard composes its link from the applicant noun because
                its hero has no page title to point at. J3 asserts the
                heading. A recruiting-style hero here breaks all three, and
                "Recruiters" is a literal on the LEASING surface — the drift
                #167 removed. That copy already lives, vertical-keyed, on
                RecruitingDashboard. */}
            <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl leading-[0.95]">
              Verification Center
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
              Applicants add identity context, account history, and people who will
              vouch for them. You see what was gathered and what is still missing,
              in one place.
            </p>
          </div>

          <div className="relative mt-6 flex flex-wrap gap-3">
            <Button
              onClick={() => setAddOpen(true)}
              className="h-11 rounded-full bg-violet-600 px-5 font-semibold text-white shadow-lg shadow-violet-500/20 hover:bg-violet-700"
            >
              Send request
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById("applicant-list")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              className="h-11 rounded-full border-slate-300 bg-white px-5 font-semibold text-slate-700 hover:bg-slate-50"
            >
              View queue
            </Button>
          </div>

          <div className="relative mt-8 grid gap-3 sm:grid-cols-3">
            {[
              { label: "Open cases", value: openCases, detail: "Currently visible in the queue." },
              { label: "Ready for review", value: reviewCount, detail: "Submitted and waiting on a decision." },
              { label: "Waiting on vouches", value: vouchCount, detail: "Still gathering supporting signals." },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {item.label}
                </div>
                <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                  {item.value}
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <Card className="overflow-hidden rounded-[2rem] border-slate-200/80 bg-white/90 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                  Applicant journey
                </div>
                <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-950">
                  A softer path from application to recruiter review.
                </h2>
              </div>
              <div className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                Joyful by design
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {JOURNEY_STEPS.map((step) => {
                const Icon = step.icon;
                const isPurple = step.title === "Trust checkpoint";
                return (
                  <div
                    key={step.title}
                    className={`rounded-2xl border p-4 ${step.tone}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                          isPurple ? "bg-white/15" : "bg-violet-50 text-violet-600"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-base font-semibold tracking-tight">{step.title}</div>
                        <p className={`mt-1 text-sm leading-6 ${isPurple ? "text-white/80" : "text-slate-600"}`}>
                          {step.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-sm leading-6 text-slate-600">
              More proof in, less chasing out. The queue stays bright, calm, and easy to scan.
            </div>
          </CardContent>
        </Card>
      </motion.section>

      {cases.length === 0 && (
        <Card className="rounded-[2rem] border-slate-200/80 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <CardContent className="grid gap-5 p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-4">
              <Badge
                variant="outline"
                className="rounded-full border-sky-200 bg-sky-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-700"
              >
                Empty queue
              </Badge>
              <div>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-950">
                  No applications yet, which means the first impression can be gorgeous.
                </h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
                  Start with a request and the page will begin filling with calmer signals, clearer context,
                  and a friendlier recruiter review flow.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => setAddOpen(true)}
                  className="h-11 rounded-full bg-violet-600 px-5 font-semibold text-white shadow-lg shadow-violet-500/20 hover:bg-violet-700"
                >
                  Send request
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    document.getElementById("applicant-list")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                  className="h-11 rounded-full border-slate-300 bg-white px-5 font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Review queue layout
                </Button>
              </div>
            </div>

            <div className="rounded-[1.75rem] bg-gradient-to-br from-violet-600 to-indigo-600 p-5 text-white shadow-[0_24px_70px_rgba(91,33,182,0.25)]">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                What the team will feel
              </div>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-white/90">
                <li className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                  Applicants feel guided, not buried in forms.
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  Recruiters get stronger signal without extra clutter.
                </li>
                <li className="flex items-start gap-2">
                  <Users className="mt-0.5 h-4 w-4 shrink-0" />
                  The review queue stays calm, clear, and human.
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <AddApplicantDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refetch} />

      <div className="space-y-3">
        <MorningSnapshot cases={cases} loading={loading} onFilter={handleSnapshotFilter} />
        <VerificationReasons
          cases={cases}
          activeReasonKey={reasonFilter?.key ?? null}
          onSelectReason={(key, label) => {
            setReasonFilter((cur) => (cur?.key === key ? null : { key, label }));
            setPage(1);
            if (typeof document !== "undefined") {
              document.getElementById("applicant-list")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }}
        />
      </div>

      {/* Queue table */}
      <motion.div {...fadeUp(0.1)} className="mt-6">
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Zone 2: Search + compact summary strip + clear all */}
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-border space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search applicants, units, or reasons"
                  className="pl-9 h-9"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                /* Stub: advanced filters panel to come. */
                onClick={() => {}}
                className="gap-2 h-9"
                aria-label="Open filters"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
              </Button>
            </div>

            {(attentionOnly || waitingOnVouchOnly || reasonFilter) && (
              <div className="flex items-center gap-2 flex-wrap">
                {attentionOnly && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 border border-orange-200">
                    Filter: Needs Attention
                    <button
                      type="button"
                      onClick={() => {
                        setAttentionOnly(false);
                        setPage(1);
                      }}
                      aria-label="Clear Needs Attention filter"
                      className="hover:text-orange-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {waitingOnVouchOnly && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-violet-50 text-violet-700 border border-violet-200">
                    Filter: Waiting on Vouch
                    <button
                      type="button"
                      onClick={() => {
                        setWaitingOnVouchOnly(false);
                        setPage(1);
                      }}
                      aria-label="Clear Waiting on Vouch filter"
                      className="hover:text-violet-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {reasonFilter && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-violet-50 text-violet-700 border border-violet-200">
                    Reason: {reasonFilter.label}
                    <button
                      type="button"
                      onClick={() => {
                        setReasonFilter(null);
                        setPage(1);
                      }}
                      aria-label="Clear reason filter"
                      className="hover:text-violet-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              <div className="flex-1 min-w-0 flex items-center gap-6 overflow-x-auto scrollbar-none border-b border-border flex-nowrap">
                {/* "All" tab — always shown */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        setStatusFilter("all");
                        setAttentionOnly(false);
                        setPage(1);
                      }}
                      className={`relative shrink-0 inline-flex items-center gap-1.5 px-1 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm -mb-px border-b-2 ${
                        statusFilter === "all"
                          ? "border-foreground text-foreground font-semibold"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      aria-pressed={statusFilter === "all"}
                      aria-label={`All — ${FILTER_TOOLTIP.all}`}
                    >
                      <span>All</span>
                      <span className="text-muted-foreground/70 tabular-nums">
                        ({cases.filter((c) => !isClosedCase(c)).length})
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    {FILTER_TOOLTIP.all}
                  </TooltipContent>
                </Tooltip>

                {/* All status tabs in priority order (show 0 when empty) */}
                {SUMMARY_ORDER.map((kind) => {
                  const label = STATUS_FILTER_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
                  const active = statusFilter === kind;
                  const count = counts[kind] ?? 0;
                  return (
                    <Tooltip key={kind}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => {
                            setStatusFilter((prev) => (prev === kind ? "all" : kind));
                            setAttentionOnly(false);
                            setPage(1);
                          }}
                          className={`relative shrink-0 inline-flex items-center gap-1.5 px-1 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm -mb-px border-b-2 ${
                            active
                              ? "border-foreground text-foreground font-semibold"
                              : "border-transparent text-muted-foreground hover:text-foreground"
                          }`}
                          aria-pressed={active}
                          aria-label={`${label} — ${FILTER_TOOLTIP[kind]}`}
                        >
                          <span>{label}</span>
                          <span className="text-muted-foreground/70 tabular-nums">({count})</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {FILTER_TOOLTIP[kind]}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="shrink-0 inline-flex items-center text-xs font-medium text-violet-700 hover:text-violet-900 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>



          {/* Column headers */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-3 bg-muted/30 border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <div className="col-span-2">Status</div>
            <div className="col-span-3 md:pl-4">Applicant</div>
            <div className="col-span-2 -ml-10">Unit / Property</div>
            <div className="col-span-3 -ml-8">What's happening</div>
            <div className="col-span-2">Last Activity</div>
          </div>

          {/* Rows */}
          <div
            id="applicant-list"
            className="divide-y divide-border"
            aria-live="polite"
            aria-busy={loading}
          >
            {loading && pageCases.length === 0 &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="px-6 py-5">
                  <div className="h-12 bg-muted/40 rounded animate-pulse" />
                </div>
              ))}

            {!loading && pageCases.length === 0 && (
              <div className="px-6 py-16 text-center" role="status">
                {cases.length === 0 ? (
                  <>
                    <p className="text-base font-bold text-foreground">No verification requests yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Send your first request to start verifying identity confidence.
                    </p>
                    <Button onClick={() => setAddOpen(true)} className="mt-4 gap-2">
                      <Plus className="w-4 h-4" />
                      Send Request
                    </Button>
                  </>
                ) : hasActiveFilters ? (
                  <>
                    <p className="text-base font-bold text-foreground">
                      {search.trim()
                        ? `No applicant matches "${search.trim()}"`
                        : "No cases match your filters"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {search.trim()
                        ? "Try a different name, clear filters, or invite them as a new applicant."
                        : "Try a different search, or clear your filters to see all applicants."}
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                      <Button variant="outline" onClick={clearFilters}>
                        Clear filters
                      </Button>
                      {search.trim() && (
                        <Button onClick={() => setAddOpen(true)} className="gap-2">
                          <Plus className="w-4 h-4" />
                          Invite as new applicant
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-base font-bold text-foreground">All caught up</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You have no open verification cases. Closed cases are hidden.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setStatusFilter("closed");
                        setPage(1);
                      }}
                      className="mt-4"
                    >
                      Show closed cases
                    </Button>
                  </>
                )}
              </div>
            )}

            {pageCases.map((c, i) => {
              const status = getCaseStatus(c);
              const id = c.inquiry_id || c.session_id || `row-${i}`;
              const lastActivity = c.updated_at ?? c.created_at;
              return (
                <motion.div
                  key={id}
                  {...fadeUp(0.12 + i * 0.03)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open verification case for ${toTitleCase(c.applicant_name)}, status ${status.pill}`}
                  onClick={() => openCase(c)}
                  onMouseEnter={() => prefetchApplicant(c)}
                  onFocus={() => prefetchApplicant(c)}

                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCase(c);
                    }
                  }}
                  className="px-4 sm:px-6 py-4 sm:py-5 grid grid-cols-1 md:grid-cols-12 md:items-center gap-3 hover:bg-muted/30 cursor-pointer transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                >
                  {/* Mobile top row: name + property + date + close/chevron */}
                  <div className="md:hidden flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-foreground group-hover:text-primary transition-colors truncate">
                        {toTitleCase(c.applicant_name)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">
                        {c.property?.trim() ? c.property : <span className="text-muted-foreground/60">—</span>}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-0.5">
                        {formatAbsoluteDate(c.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCloseTarget(c);
                        }}
                        className="p-2 text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 rounded-lg transition-all"
                        title="Close case"
                        aria-label={`Close case for ${toTitleCase(c.applicant_name)}`}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                    </div>
                  </div>

                  {/* Status pill (no tooltip on rows — keeps the queue scannable) */}
                  <div className="md:col-span-2">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-md ${status.pillClass}`}
                      aria-label={`Status: ${status.pill}`}
                    >
                      <status.Icon
                        className={`w-3.5 h-3.5 shrink-0 ${status.iconSpin ? "animate-spin" : ""}`}
                        aria-hidden="true"
                      />
                      <span>{status.pill}</span>
                    </span>
                  </div>



                  {/* Applicant (desktop only — mobile shows above) */}
                  <div className="hidden md:block md:col-span-3 md:pl-4">
                    <p className="font-bold text-foreground group-hover:text-primary transition-colors">
                      {toTitleCase(c.applicant_name)}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-sm text-muted-foreground">
                        {formatAbsoluteDate(c.created_at)}
                      </p>
                      {/*
                        BF-B5 — the IDV states the operator has to act on ride here
                        rather than in a 6th column: the 12-col grid is fully
                        allocated and the three middle columns are contractually
                        distinct (CLAUDE.md, Verification Center). Only
                        declined / incomplete surface — see isQueueWorthyIdvState.
                      */}
                      {(() => {
                        // platform#595: a required ID check that went unmet marks
                        // the row Review Required (a marker, not a status pill;
                        // see lib/requiredIdv.ts). It replaces the IDV badge
                        // rather than sitting beside it, so the row carries one
                        // identity marker and the reason rides on hover.
                        if (isUnderRequiredIdvReview(c.required_idv)) {
                          return (
                            <ApplicantIDVStateBadge
                              label={REQUIRED_IDV_REVIEW_LABEL}
                              tone="attention"
                              compact
                              title={c.required_idv?.reason ?? undefined}
                            />
                          );
                        }
                        const idv = isQueueWorthyIdvState(c.applicant_idv_state ?? null)
                          ? describeApplicantIdvState(c.applicant_idv_state ?? null, {
                              requiredByFlow: c.required_idv?.required === true,
                            })
                          : null;
                        if (!idv) return null;
                        return (
                          <ApplicantIDVStateBadge
                            label={idv.label}
                            tone={idv.tone}
                            compact
                            title={idv.detail ?? undefined}
                          />
                        );
                      })()}
                    </div>
                  </div>

                  {/* Unit / Property (desktop only) */}
                  <div className="hidden md:block md:col-span-2 md:-ml-10 min-w-0">
                    <p
                      className="text-sm text-foreground truncate"
                      title={c.property ?? ""}
                    >
                      {c.property?.trim() ? c.property : <span className="text-muted-foreground/60">—</span>}
                    </p>
                  </div>

                  {/* What's happening */}
                  <div className="md:col-span-3 md:-ml-8 flex items-center min-w-0">
                    <span className="text-sm font-normal text-muted-foreground">
                      {buildWhatsHappening(c)}
                    </span>
                  </div>

                  {/* Last Activity */}
                  <div className="md:col-span-2 flex items-center justify-between gap-2">
                    <div
                      className="text-xs sm:text-sm min-w-0"
                      title={formatAbsoluteDateTime(lastActivity)}
                    >
                      <p className="md:hidden text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Last Activity
                      </p>
                      <p className="text-sm font-medium text-muted-foreground truncate">{formatRelativeTime(lastActivity)}</p>
                    </div>
                    {/* Close + chevron on desktop only */}
                    <div className="hidden md:flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCloseTarget(c);
                        }}
                        className="p-2 text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 rounded-lg transition-all"
                        title="Close case"
                        aria-label={`Close case for ${toTitleCase(c.applicant_name)}`}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </motion.div>
              );
            })}

          </div>

          {/* Pagination footer */}
          {sorted.length > 0 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Showing {startIdx + 1} to {endIdx} of {sorted.length} applicants
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="p-2 rounded-md border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const n = idx + 1;
                  const active = n === safePage;
                  return (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`min-w-8 h-8 px-2 rounded-md text-sm font-semibold ${
                        active
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted/50"
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="p-2 rounded-md border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile FAB */}
        <button
          onClick={() => setAddOpen(true)}
          className="md:hidden fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center"
          aria-label="Send Request"
        >
          <Plus className="w-6 h-6" />
        </button>
      </motion.div>

      <AlertDialog
        open={!!closeTarget}
        onOpenChange={(o) => {
          if (closing || o) return;
          setCloseTarget(null);
          setCloseReason("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this case?</AlertDialogTitle>
            <AlertDialogDescription>
              {closeTarget
                ? `Closing will archive the verification request for ${toTitleCase(closeTarget.applicant_name)}. You can reopen it later from closed cases.`
                : "Closing will archive the verification request. You can reopen it later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="close-reason"
              className="text-xs font-medium text-muted-foreground"
            >
              Reason (optional)
            </label>
            <Textarea
              id="close-reason"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Applicant non-responsive, duplicate submission, withdrew application…"
              maxLength={500}
              rows={3}
              disabled={closing}
              className="resize-none text-sm"
            />
            <div className="text-[10px] text-muted-foreground text-right">
              {closeReason.length}/500
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmClose();
              }}
              disabled={closing}
            >
              {closing ? "Closing…" : "Close case"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
